import { DndContext, DragOverlay, pointerWithin, useDroppable } from "@dnd-kit/core";
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { DragEvent, MouseEvent, ReactNode } from "react";
import { Text } from "@mantine/core";
import { IconChevronRight, IconSquare, IconSquareCheckFilled } from "@tabler/icons-react";
import clsx from "clsx";

import { useFileManager, useFileManagerUI } from "@/components/core/FileManager";
import type {
  FileManagerItem,
  FileManagerQueryContext,
  UploadFileItem,
} from "@/components/core/FileManager";
import { FileManagerItemContextMenu } from "@/components/core/FileManager";
import { FileManagerItemDndWrapper } from "@/components/core/FileManager";
import { FileManagerItemDefaultMenuItems } from "../FileManagerContextMenu/FileManagerItemMenuItems";
import {
  FileManagerDragOverlayCard,
  useFileManagerDndActive,
  useFileManagerDndHandlers,
} from "../FileManagerDndProvider/FileManagerDndProvider";
import { getIconConfig } from "../FileManagerItemCard/FileManagerItemCard";

import styles from "./FileManagerColumnView.module.scss";

/**
 * `undefined`  — no OS file drag active, or pointer is over a folder item (not the column background)
 * `null`       — pointer is over the root column background
 * `string`     — pointer is over a subfolder column background (value = folder id)
 */
type TColumnDragTarget = string | null | undefined;

function isFileDrag(event: DragEvent): boolean {
  return Array.from(event.dataTransfer.types).includes("Files");
}

function buildColumnPath(
  columns: Column[],
  throughIndex: number,
  append?: { id: string; name: string },
): { id: string; name: string }[] {
  const base = columns
    .slice(0, throughIndex + 1)
    .filter((col) => col.id !== null)
    .map((col) => ({ id: col.id!, name: col.name }));
  return append ? [...base, append] : base;
}

function getHoveredFolderId(target: EventTarget | null): string | null {
  const folderEl = (target as HTMLElement | null)?.closest<HTMLElement>(
    "[data-item-type='FOLDER']",
  );
  return folderEl?.dataset.itemId ?? null;
}

function getHoveredColumnId(target: EventTarget | null): string | null {
  const colEl = (target as HTMLElement | null)?.closest<HTMLElement>("[data-col-id]");
  if (!colEl) return null;
  const colId = colEl.dataset.colId;
  return colId === "root" ? null : (colId ?? null);
}

function resolveFileDragPosition(eventTarget: EventTarget | null): {
  folderId: string | null;
  colId: TColumnDragTarget;
} {
  const folderId = getHoveredFolderId(eventTarget);
  return {
    folderId,
    colId: folderId ? undefined : getHoveredColumnId(eventTarget),
  };
}

interface Column {
  id: string | null;
  name: string;
  items: FileManagerItem[];
  loading: boolean;
}

export interface FileManagerColumnViewProps {
  fetchItems: (ctx: FileManagerQueryContext) => Promise<FileManagerItem[]>;
  columnWidth?: number;
  renderItem?: (
    item: FileManagerItem,
    colIndex: number,
    isOsDragOver: boolean,
    onOpen: (item: FileManagerItem) => void,
  ) => ReactNode;
  renderColumn?: (content: ReactNode, colIndex: number, colFolderId: string | null) => ReactNode;
  hideApproveForColumn?: (column: { id: string | null; name: string }) => boolean;
}

export function FileManagerColumnView({
  fetchItems,
  columnWidth = 220,
  renderItem,
  renderColumn,
  hideApproveForColumn,
}: FileManagerColumnViewProps) {
  const {
    items: contextItems,
    isLoading: contextLoading,
    currentFolderId,
    breadcrumb,
    search,
    upload,
    openFolder,
    mutationVersion,
  } = useFileManager();
  const { isSelected } = useFileManagerUI();
  const hasDndProvider = useFileManagerDndActive();
  const { activeItem, dragCount, sensors, handleDragStart, handleDragEnd } =
    useFileManagerDndHandlers();

  const fetchItemsRef = useRef(fetchItems);
  const breadcrumbRef = useRef(breadcrumb);
  const columnsRef = useRef<Column[]>([]);
  useLayoutEffect(() => {
    fetchItemsRef.current = fetchItems;
    breadcrumbRef.current = breadcrumb;
    columnsRef.current = columns;
  });

  const internalNavTargetRef = useRef<{ folderId: string | null } | null>(null);

  const [columns, setColumns] = useState<Column[]>([
    {
      id: currentFolderId,
      name: breadcrumb.at(-1)?.name ?? "Root",
      items: contextItems,
      loading: contextLoading,
    },
  ]);

  const [activeItemIds, setActiveItemIds] = useState<Record<number, string>>({});
  const [activeColIndex, setActiveColIndex] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileDragHoverFolderId, setFileDragHoverFolderId] = useState<string | null>(null);
  const [fileDragHoverColId, setFileDragHoverColId] = useState<TColumnDragTarget>(undefined);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = internalNavTargetRef.current;
    if (target !== null && currentFolderId === target.folderId) {
      if (search === null) internalNavTargetRef.current = null;
      return;
    }
    internalNavTargetRef.current = null;

    let cancelled = false;
    setColumns([
      {
        id: currentFolderId,
        name: breadcrumb.at(-1)?.name ?? "Root",
        items: [],
        loading: true,
      },
    ]);
    setActiveItemIds({});
    setActiveColIndex(null);

    fetchItemsRef
      .current({ currentFolderId, breadcrumb: breadcrumbRef.current, search: search ?? null })
      .then((items) => {
        if (cancelled) return;
        setColumns([
          {
            id: currentFolderId,
            name: breadcrumbRef.current.at(-1)?.name ?? "Root",
            items,
            loading: false,
          },
        ]);
      })
      .catch(() => {
        if (cancelled) return;
        setColumns([
          {
            id: currentFolderId,
            name: breadcrumbRef.current.at(-1)?.name ?? "Root",
            items: [],
            loading: false,
          },
        ]);
      });

    return () => {
      cancelled = true;
    };
  }, [currentFolderId, search]);

  useEffect(() => {
    setColumns((prev) => {
      const idx = prev.findIndex((col) => col.id === currentFolderId);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx]!, items: contextItems, loading: contextLoading };
      return next;
    });
  }, [contextItems, contextLoading, currentFolderId]);

  const prevMutationVersionRef = useRef(mutationVersion);
  useEffect(() => {
    if (mutationVersion === prevMutationVersionRef.current) return;
    prevMutationVersionRef.current = mutationVersion;

    let cancelled = false;
    const cols = columnsRef.current;
    setColumns((prev) => prev.map((col) => ({ ...col, loading: true })));

    cols.forEach((col, idx) => {
      fetchItemsRef
        .current({ currentFolderId: col.id, breadcrumb: [], search: null })
        .then((items) => {
          if (cancelled) return;
          setColumns((prev) => {
            if (prev[idx]?.id !== col.id) return prev;
            const next = [...prev];
            next[idx] = { ...next[idx]!, items, loading: false };
            return next;
          });
        })
        .catch(() => {
          if (cancelled) return;
          setColumns((prev) => {
            if (prev[idx]?.id !== col.id) return prev;
            const next = [...prev];
            next[idx] = { ...next[idx]!, loading: false };
            return next;
          });
        });
    });

    return () => {
      cancelled = true;
    };
  }, [mutationVersion]);

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ left: scrollRef.current.scrollWidth, behavior: "smooth" });
    });
  }, []);

  const handleFolderOpen = useCallback(
    async (item: FileManagerItem, colIndex: number) => {
      if (item.type !== "FOLDER") return;

      const path = buildColumnPath(columnsRef.current, colIndex, { id: item.id, name: item.name });

      internalNavTargetRef.current = { folderId: item.id };
      openFolder(item.id, item.name, path);

      setActiveItemIds((prev) => {
        const next: Record<number, string> = {};
        for (let i = 0; i < colIndex; i++) {
          if (prev[i] !== undefined) next[i] = prev[i]!;
        }
        next[colIndex] = item.id;
        return next;
      });

      const newCol: Column = { id: item.id, name: item.name, items: [], loading: true };
      setColumns((prev) => [...prev.slice(0, colIndex + 1), newCol]);
      setActiveColIndex(colIndex + 1);
      scrollToEnd();

      try {
        const items = await fetchItemsRef.current({
          currentFolderId: item.id,
          breadcrumb: [],
          search: null,
        });
        setColumns((prev) => {
          const next = [...prev];
          if (next[colIndex + 1]?.id === item.id) {
            next[colIndex + 1] = { ...newCol, items, loading: false };
          }
          return next;
        });
        scrollToEnd();
      } catch {
        setColumns((prev) => {
          const next = [...prev];
          if (next[colIndex + 1]?.id === item.id) {
            next[colIndex + 1] = { ...newCol, loading: false };
          }
          return next;
        });
        setActiveColIndex(null);
      }
    },
    [openFolder, scrollToEnd],
  );

  const handleItemClick = useCallback(
    (item: FileManagerItem, colIndex: number) => {
      if (item.type === "FOLDER") {
        void handleFolderOpen(item, colIndex);
      } else {
        setActiveColIndex(colIndex);
        setActiveItemIds((prev) => {
          const next: Record<number, string> = {};
          for (let i = 0; i < colIndex; i++) {
            if (prev[i] !== undefined) next[i] = prev[i]!;
          }
          next[colIndex] = item.id;
          return next;
        });
        setColumns((prev) => prev.slice(0, colIndex + 1));

        const col = columnsRef.current[colIndex];
        if (col !== undefined) {
          internalNavTargetRef.current = { folderId: col.id };
          if (col.id === null) {
            openFolder(null);
          } else {
            openFolder(col.id, col.name, buildColumnPath(columnsRef.current, colIndex));
          }
        }
      }
    },
    [handleFolderOpen, openFolder],
  );

  function updateFileDragPosition(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return false;
    event.preventDefault();
    const { folderId, colId } = resolveFileDragPosition(event.target);
    setFileDragHoverFolderId(folderId);
    setFileDragHoverColId(colId);
    return true;
  }

  function handleFileDragEnter(event: DragEvent<HTMLDivElement>) {
    if (updateFileDragPosition(event)) setIsDragOver(true);
  }

  function handleFileDragOver(event: DragEvent<HTMLDivElement>) {
    updateFileDragPosition(event);
  }

  function handleFileDragLeave(event: DragEvent<HTMLDivElement>) {
    if (!isFileDrag(event)) return;
    if ((event.currentTarget as HTMLDivElement).contains(event.relatedTarget as Node | null))
      return;
    setIsDragOver(false);
    setFileDragHoverFolderId(null);
    setFileDragHoverColId(undefined);
  }

  function handleFileDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    const targetFolderId =
      getHoveredFolderId(event.target) ?? getHoveredColumnId(event.target) ?? undefined;
    setFileDragHoverFolderId(null);
    setFileDragHoverColId(undefined);

    if (droppedFiles.length === 0) return;

    const uploadItems: UploadFileItem[] = droppedFiles.map((file) => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    upload(uploadItems, targetFolderId);
  }

  const columnsContent = (
    <div
      ref={scrollRef}
      data-slot="file-manager-column-view"
      className={styles.columns}
      style={{ "--column-width": `${columnWidth}px` } as React.CSSProperties}
      onDragEnter={handleFileDragEnter}
      onDragOver={handleFileDragOver}
      onDragLeave={handleFileDragLeave}
      onDrop={handleFileDrop}
    >
      {isDragOver && !fileDragHoverFolderId && fileDragHoverColId !== undefined && (
        <div className={styles.dropOverlay}>
          <Text fw={500} size="lg" style={{ color: "var(--text-brand-default)" }}>
            Drop files to upload
          </Text>
        </div>
      )}
      {columns.map((col, colIndex) => {
        const colItems = col.loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner} />
          </div>
        ) : col.items.length === 0 ? (
          <p className={styles.empty}>Empty folder</p>
        ) : (
          col.items.map((item) => {
            const isOsDragOver = fileDragHoverFolderId === item.id;
            const onOpen = (i: FileManagerItem) => handleItemClick(i, colIndex);

            if (renderItem) {
              return (
                <div key={item.id} style={{ display: "contents" }}>
                  {renderItem(item, colIndex, isOsDragOver, onOpen)}
                </div>
              );
            }

            const hideApprove = hideApproveForColumn?.({ id: col.id, name: col.name }) ?? false;

            return (
              <FileManagerItemDndWrapper key={item.id} item={item} isOsDragOver={isOsDragOver}>
                <FileManagerItemContextMenu
                  item={item}
                  menuContent={
                    <FileManagerItemDefaultMenuItems item={item} hideApprove={hideApprove} />
                  }
                >
                  <ColumnRowItem
                    item={item}
                    isActive={activeItemIds[colIndex] === item.id}
                    isSelected={isSelected(item.id)}
                    onOpen={onOpen}
                  />
                </FileManagerItemContextMenu>
              </FileManagerItemDndWrapper>
            );
          })
        );

        const columnDiv = (
          <ColumnDropZone
            colId={col.id}
            isActive={colIndex === activeColIndex}
            isOsDragOver={isDragOver && !fileDragHoverFolderId && fileDragHoverColId === col.id}
          >
            {colItems}
          </ColumnDropZone>
        );

        return (
          <Fragment key={col.id ?? "root"}>
            {renderColumn ? renderColumn(columnDiv, colIndex, col.id) : columnDiv}
            {colIndex < columns.length - 1 && <div className={styles.columnDivider} />}
          </Fragment>
        );
      })}
    </div>
  );

  if (hasDndProvider) {
    return columnsContent;
  }

  return (
    <DndContext
      collisionDetection={pointerWithin}
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {columnsContent}
      <DragOverlay dropAnimation={null}>
        {activeItem ? <FileManagerDragOverlayCard item={activeItem} count={dragCount} /> : null}
      </DragOverlay>
    </DndContext>
  );
}

function ColumnDropZone({
  colId,
  isActive,
  isOsDragOver,
  children,
}: {
  colId: string | null;
  isActive: boolean;
  isOsDragOver: boolean;
  children: ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `col-zone-${colId ?? "root"}`,
    data: { colFolderId: colId },
  });
  return (
    <div
      ref={setNodeRef}
      className={clsx(
        styles.column,
        isActive && styles.columnActive,
        (isOver || isOsDragOver) && styles.columnDragOver,
      )}
      data-col-id={colId ?? "root"}
    >
      {children}
    </div>
  );
}

function ColumnRowItem({
  item,
  isActive,
  isSelected,
  onOpen,
}: {
  item: FileManagerItem;
  isActive: boolean;
  isSelected: boolean;
  onOpen: (item: FileManagerItem) => void;
}) {
  const { isSelectionMode, toggleItem } = useFileManagerUI();
  const { Icon, color } = getIconConfig(item);

  const checkboxColor = isActive ? "white" : "var(--bg-brand-default)";
  const checkboxIdleColor = isActive ? "rgba(255,255,255,0.45)" : "var(--text-disabled)";

  function handleClick(e: MouseEvent<HTMLButtonElement>) {
    if (isSelectionMode || e.ctrlKey || e.metaKey) {
      e.preventDefault();
      toggleItem(item);
      return;
    }
    onOpen(item);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen(item);
    }
  }

  return (
    <button
      type="button"
      className={clsx(
        styles.item,
        isActive && styles.itemActive,
        isSelected && !isActive && styles.itemSelected,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={item.name}
      aria-pressed={isSelected}
    >
      <span className={styles.iconSlot}>
        <span
          className={clsx(styles.checkboxIcon, isSelected && styles.checkboxVisible)}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleItem(item);
          }}
        >
          {isSelected ? (
            <IconSquareCheckFilled size={14} color={checkboxColor} />
          ) : (
            <IconSquare size={14} color={checkboxIdleColor} />
          )}
        </span>
        <Icon size={15} color={isActive ? "white" : color} />
      </span>
      <span className={styles.itemName}>{item.name}</span>
      {item.type === "FOLDER" && (
        <IconChevronRight
          size={13}
          className={clsx(styles.chevron, isActive && styles.chevronActive)}
        />
      )}
    </button>
  );
}
